import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronRight, Play, Check, X, Zap, Sun, Truck,
  Building2, Activity, BatteryCharging, Cpu, Wifi, Cloud,
  CircleDot, MapPin,
} from "lucide-react";
import {
  STATS, TRUSTED_BY, PROBLEM_COMPARISON, MBESS_MODELS, FEATURES,
  APPLICATIONS, WHY_TABLE, DEPLOY_LOCATIONS, TESTIMONIALS,
} from "@/lib/data";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/* ────────────────── HERO ────────────────── */
export function Hero() {
  const ref = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section ref={ref} className="relative min-h-[100vh] overflow-hidden noise" data-testid="hero-section">
      <motion.div style={{ y, opacity }} className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-grid bg-grid-fade" />
        <img
          src="https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=2000&q=80&auto=format&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-tratou-void/40 via-tratou-void/70 to-tratou-void" />
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 lg:pt-36 pb-24 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-5xl"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <CircleDot className="w-3 h-3" /> Mobile Battery Energy Storage · MBESS
          </span>
          <h1 className="heading-massive mt-6">
            Power Anywhere.
            <br />
            <span className="text-tratou-green">Without Waiting</span> for the Grid.
          </h1>
          <p className="mt-8 max-w-2xl text-tratou-dim text-lg leading-relaxed">
            Tratou Energy delivers portable renewable electricity using Mobile Battery Energy Storage Systems
            (MBESS), enabling clean power wherever and whenever it is needed — from remote villages to
            construction sites, mining operations, and emergency response.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact?intent=demo" className="btn-primary" data-testid="hero-cta-demo">
              Book Live Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact?intent=quote" className="btn-secondary" data-testid="hero-cta-quote">
              Get a Quote
            </Link>
            <button className="btn-ghost" onClick={() => setVideoOpen(true)} data-testid="hero-cta-video">
              <Play className="w-4 h-4 text-tratou-green" /> Watch Video
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-tratou-void p-5">
              <p className="font-mono text-[10px] tracking-[0.2em] text-tratou-green uppercase">{s.label}</p>
              <p className="mt-2 font-display font-bold text-base md:text-lg tracking-tight">{s.value}</p>
            </div>
          ))}
        </motion.div>

        {/* Battery charging mini-viz */}
        <div className="mt-16 max-w-md">
          <div className="flex items-center justify-between mb-2">
            <span className="eyebrow-dim">MBESS CHARGE STATUS</span>
            <span className="font-mono text-xs text-tratou-green">98%</span>
          </div>
          <div className="charge-bar">
            <span className="animate-charge" style={{ width: "98%" }} />
          </div>
        </div>
      </div>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent
          className="max-w-5xl w-[95vw] p-0 bg-tratou-void border border-tratou-green/30 rounded-none"
          data-testid="hero-video-dialog"
        >
          <DialogTitle className="sr-only">Tratou Energy Hero Video</DialogTitle>
          <DialogDescription className="sr-only">
            Overview of Tratou Energy Mobile Battery Energy Storage Systems and Energy-as-a-Service in the field.
          </DialogDescription>
          <div className="aspect-video bg-black">
            {videoOpen && (
              <video
                src="/videos/tratou-hero.mp4"
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                data-testid="hero-video-player"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* ────────────────── TRUSTED BY ────────────────── */
export function TrustedBy() {
  const items = [...TRUSTED_BY, ...TRUSTED_BY];
  return (
    <section className="py-16 border-y border-white/10 marquee overflow-hidden" data-testid="trusted-by">
      <p className="text-center eyebrow-dim mb-6">Trusted by future industry leaders</p>
      <div className="marquee-track gap-16 px-10">
        {items.map((logo, i) => (
          <div key={i} className="font-display font-bold text-2xl text-tratou-dim/70 hover:text-tratou-green transition shrink-0">
            {logo}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ────────────────── PROBLEM ────────────────── */
export function Problem() {
  return (
    <section className="py-28" data-testid="problem-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="eyebrow">The Problem</span>
          <h2 className="heading-section mt-4">
            Why diesel generators are <span className="text-tratou-green">becoming obsolete.</span>
          </h2>
          <p className="mt-6 text-tratou-dim text-lg">
            For over a century, diesel has powered the world's remote operations. But the economics, ESG
            scorecards and customer expectations have changed. The math no longer works.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <div className="bg-tratou-deep p-8 lg:p-10">
            <p className="eyebrow-dim mb-4">DIESEL GENERATOR</p>
            <h3 className="font-display text-3xl font-bold mb-6 text-white/70">The legacy way</h3>
            <ul className="space-y-3">
              {PROBLEM_COMPARISON.diesel.map((it) => (
                <li key={it} className="flex items-start gap-3 text-tratou-dim">
                  <X className="w-4 h-4 mt-1 text-red-400 flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-tratou-deep p-8 lg:p-10 relative">
            <div className="absolute -top-px left-0 right-0 divider-grad lg:hidden" />
            <p className="eyebrow mb-4">TRATOU MBESS</p>
            <h3 className="font-display text-3xl font-bold mb-6">The new default</h3>
            <ul className="space-y-3">
              {PROBLEM_COMPARISON.mbess.map((it) => (
                <li key={it} className="flex items-start gap-3 text-white">
                  <Check className="w-4 h-4 mt-1 text-tratou-green flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────── SOLUTION ────────────────── */
export function Solution() {
  return (
    <section className="py-28 relative" data-testid="solution-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="eyebrow">The Solution</span>
          <h2 className="heading-section mt-4">Meet the MBESS.</h2>
          <p className="mt-6 text-tratou-dim text-lg">
            A modular, mobile, AI-monitored Mobile BESS &amp; Charging Station platform. Six sizes from
            106 kWh to 1.29 MWh. One mission: deliver clean kilowatt-hours wherever they are needed.
          </p>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {MBESS_MODELS.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-tratou-deep p-8 panel-hover relative group"
              data-testid={`mbess-card-${m.name.toLowerCase()}`}
            >
              <div className="aspect-video mb-6 overflow-hidden border border-white/5">
                <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              </div>
              <p className="eyebrow-dim">{m.name}</p>
              <p className="mt-2 font-display text-4xl font-bold text-tratou-green">{m.capacity}</p>
              <div className="mt-5 grid grid-cols-2 gap-3 font-mono text-xs">
                <Spec k="PEAK" v={m.peak} />
                <Spec k="WEIGHT" v={m.weight} />
                <Spec k="FOOTPRINT" v={m.footprint} />
                <Spec k="USE" v={m.apps} />
              </div>
              <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-tratou-green hover:gap-3 transition-all">
                Specifications <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {FEATURES.map(({ icon: Icon, label }) => (
            <div key={label} className="bg-tratou-deep p-5 flex items-center gap-3 panel-hover">
              <Icon className="w-5 h-5 text-tratou-green" />
              <span className="text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Spec({ k, v }) {
  return (
    <div>
      <p className="text-[10px] text-tratou-faint tracking-[0.2em] uppercase">{k}</p>
      <p className="mt-0.5 text-white text-xs">{v}</p>
    </div>
  );
}

/* ────────────────── APPLICATIONS ────────────────── */
export function Applications() {
  return (
    <section className="py-28" data-testid="applications-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="eyebrow">Applications</span>
            <h2 className="heading-section mt-4">One platform. <span className="text-tratou-green">Every site.</span></h2>
          </div>
          <Link to="/industries" className="btn-secondary self-start" data-testid="applications-view-all">
            All Industries <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {APPLICATIONS.map(({ icon: Icon, label, image }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
              className="relative bg-tratou-deep aspect-square overflow-hidden group panel-hover"
              data-testid={`app-card-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-110 transition duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-tratou-void via-tratou-void/40 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <Icon className="w-5 h-5 text-tratou-green mb-2" />
                <p className="font-display font-bold text-lg">{label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────── EAAS FLOW ────────────────── */
export function EaasFlow() {
  const STEPS = [
    { icon: Sun, label: "Solar Farm", sub: "Renewable generation" },
    { icon: BatteryCharging, label: "MBESS", sub: "Stored & conditioned" },
    { icon: Truck, label: "Logistics", sub: "Trailer / container" },
    { icon: Building2, label: "Customer Site", sub: "On-demand delivery" },
    { icon: Zap, label: "Electricity", sub: "Per-kWh billing" },
  ];
  return (
    <section className="py-28 relative" data-testid="eaas-flow-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="eyebrow">Energy-as-a-Service</span>
          <h2 className="heading-section mt-4">No CapEx. No diesel. <span className="text-tratou-green">Just kilowatt-hours.</span></h2>
          <p className="mt-6 text-tratou-dim text-lg">
            We own the asset. You pay for delivered electricity — per kWh, with full SLA. Tratou handles
            charging, logistics, monitoring and maintenance, end-to-end.
          </p>
        </div>

        <div className="mt-16 relative">
          <svg className="hidden md:block absolute top-1/2 left-0 right-0 -translate-y-1/2 w-full h-2 pointer-events-none">
            <line x1="5%" x2="95%" y1="50%" y2="50%" className="energy-line" />
          </svg>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 relative">
            {STEPS.map(({ icon: Icon, label, sub }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="text-center"
              >
                <div className="mx-auto w-16 h-16 border border-tratou-green/40 bg-tratou-void flex items-center justify-center relative">
                  <Icon className="w-7 h-7 text-tratou-green" />
                </div>
                <p className="mt-4 font-display font-bold">{label}</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.18em] uppercase text-tratou-dim">{sub}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {["Pay only for electricity", "No CapEx required", "No maintenance burden", "No generator on-site"].map((b) => (
            <div key={b} className="bg-tratou-deep p-6">
              <Check className="w-5 h-5 text-tratou-green mb-3" />
              <p className="font-display font-bold">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────── WHY TRATOU TABLE ────────────────── */
export function WhyTratou() {
  return (
    <section className="py-28" data-testid="why-tratou">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow">Comparison</span>
          <h2 className="heading-section mt-4">Why Tratou.</h2>
          <p className="mt-6 text-tratou-dim text-lg">
            We benchmarked MBESS against the two incumbent power solutions across every metric that matters.
          </p>
        </div>

        <div className="overflow-x-auto panel">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-6 py-4 eyebrow-dim">Metric</th>
                <th className="text-left px-6 py-4 eyebrow">Tratou MBESS</th>
                <th className="text-left px-6 py-4 eyebrow-dim">Diesel Generator</th>
                <th className="text-left px-6 py-4 eyebrow-dim">Grid Extension</th>
              </tr>
            </thead>
            <tbody>
              {WHY_TABLE.map((row, i) => (
                <tr key={row.metric} className={`border-b border-white/5 ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                  <td className="px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] text-tratou-dim">{row.metric}</td>
                  <td className="px-6 py-4 font-display font-bold text-tratou-green">{row.tratou}</td>
                  <td className="px-6 py-4 text-tratou-dim">{row.diesel}</td>
                  <td className="px-6 py-4 text-tratou-dim">{row.grid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ────────────────── GLOBAL MAP ────────────────── */
export function GlobalMap() {
  return (
    <section className="py-28" data-testid="global-map">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow">Global Deployment</span>
          <h2 className="heading-section mt-4">Powering 18+ countries. <span className="text-tratou-green">And counting.</span></h2>
        </div>
        <div className="panel relative aspect-[16/9] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=2000&q=80&auto=format&fit=crop"
            alt="World map"
            className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity"
          />
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
          {DEPLOY_LOCATIONS.map((p) => (
            <div key={p.region} className="absolute" style={{ top: p.y, left: p.x }} data-testid={`map-pin-${p.region.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="relative">
                <div className="pulse-dot" />
                <span className="absolute left-4 top-0 whitespace-nowrap font-mono text-[10px] tracking-[0.18em] uppercase text-white bg-tratou-void/80 border border-tratou-green/40 px-2 py-1">
                  {p.region}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────── TESTIMONIALS ────────────────── */
export function Testimonials() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 6500);
    return () => clearInterval(t);
  }, []);
  const t = TESTIMONIALS[idx];
  return (
    <section className="py-28 border-y border-white/10" data-testid="testimonials">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <span className="eyebrow">Testimonials</span>
        <motion.blockquote
          key={idx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-8 max-w-4xl"
        >
          <p className="font-display font-light text-2xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
            <span className="text-tratou-green">“</span>{t.quote}<span className="text-tratou-green">”</span>
          </p>
          <footer className="mt-8 font-mono text-xs tracking-[0.2em] uppercase">
            <span className="text-white">{t.author}</span>
            <span className="text-tratou-dim"> · {t.company}</span>
          </footer>
        </motion.blockquote>
        <div className="mt-10 flex gap-2">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1 w-10 ${i === idx ? "bg-tratou-green" : "bg-white/15"}`}
              aria-label={`Testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
